import { Button, Checkbox, Col, Form, FormInstance, Input, InputNumber, Radio, Select, Tag, Row } from "antd";
import { REQUIRED_RULE } from "../../../../../utils/form.validation";
import SimpleImageUplaod, { UPLOAD_TYPES } from "../../../../../components/Forms/SimpleImageUpload";
import TextArea from "antd/lib/input/TextArea";
import { Shrine } from "@a4b/api/src/modules/Temple/types";
import styled, { createGlobalStyle } from "styled-components";
import { useAppContext } from "../../../../../components/AppContext";
import { v4 as uuidv4 } from "uuid";
import { formatJson } from "@/utils/helper";
import { useCallback, useEffect, useMemo, useState } from "react";
import { renderImage } from "../../../../../components/TableHelpers/ColumnHelper";
import { useParams } from "react-router-dom";

const SubmitButton = styled(Button)`
  position: absolute;
  top: -66px;
  right: 16px;
  z-index: 1001;
  height: 32px;
  line-height: 32px;
  text-align: center;
  padding: 0 15px;
`;

const DrawerBodyFix = createGlobalStyle`
  .ant-drawer-body, .ant-modal-body {
    overflow: visible !important;
    position: relative;
    z-index: 2;
  }
`;

/** Outer panel stays WHITE now */
const VariantPanel = styled.div`
  background: transparent;     /* keep outside white */
  border: 0;
  padding: 4px 0;
  margin: 8px 0 16px;
`;

/** Grey cards for grouped fields */
const FieldCard = styled.div`
  background: #f5f5f5;           /* grey inside the card */
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 12px;
  margin-bottom: 12px;
`;

const CardTitle = styled.div`
  font-weight: 600;
  margin-bottom: 8px;
`;

const FieldRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const TARGET_GOD_ID = "ae52c60b-7e4b-4354-8ecc-b5988e627623";
type TTemple = { id: string; temple_name: string };

interface CreateAvatarFormProps {
    form: FormInstance<Shrine.AvatarPayload>;
    formData?: Shrine.AvatarPayload | any;
    showSubmitBtn?: boolean;
    disabledActive?: boolean;
    mode?: 'create' | 'update' | 'clone';
}

const CreateAvatarForm = ({ form, formData, showSubmitBtn = true, mode, disabledActive }: CreateAvatarFormProps) => {
    const { networkInstance, countryState, languageState } = useAppContext();
    const { god_id } = useParams<{ god_id: string }>();
    const shouldShowTempleDropdown = god_id === TARGET_GOD_ID;
    const isLockedCore = mode === 'update';
    const submitLabel = mode === 'clone' ? 'Clone Avatar' : formData ? 'Update Avatar' : 'Create Avatar';

    async function s3UplaodApi(
        fileType?: { type: string } | undefined,
        options?: any
    ): Promise<{ url: string; path: string; type?: string | undefined }> {
        const res = await networkInstance.clientWithHeaders.shrineApi.getS3SignedUrl(
            uuidv4() + options?.file?.name,
            "system_gods",
            form.getFieldValue("code")
        );
        return res.data.data.info;
    }

    const type = Form.useWatch("type", form);
    const access_type = Form.useWatch("access_type", form);

    const safeJsonParse = (v: any) => {
        if (typeof v !== "string") return v;
        try { return JSON.parse(v); } catch { return undefined; }
    };

    // Resolve preview from either live form value (if ever set) or formData fallback.
    // In your case, we expect preview to come from formData in Edit mode.
    const getShareMediaUrl = (lang: "en" | "hi") => {
        // Try reading any live value first (in case you ever set metadata into form later)
        const liveMeta = form.getFieldValue("metadata");
        const meta = safeJsonParse(liveMeta) ?? safeJsonParse(formData?.metadata);
        return meta?.share_media?.[lang]?.url as string | undefined;
    };

    const enPreviewUrl = getShareMediaUrl("en");
    const hiPreviewUrl = getShareMediaUrl("hi");
    console.log('media_url', enPreviewUrl)

    // ---------- Temples state / loading ----------
    const [temples, setTemples] = useState<TTemple[]>([]);
    const [loadingTemples, setLoadingTemples] = useState(false);

    const normalizeId = (v?: string | number | null) => (v == null ? undefined : String(v));

    const parseTempleList = (res: any): { items: TTemple[]; count: number } => {
        const data = res?.data?.data ?? {};
        const itemsRaw = Array.isArray(data.items) ? data.items
            : Array.isArray(data.temples) ? data.temples
                : [];
        const items: TTemple[] = itemsRaw.map((t: any) => ({
            id: String(t.id),
            temple_name: String(t.temple_name ?? t.name ?? ""),
        }));
        const count = typeof data.count === "number" ? data.count : items.length;
        return { items, count };
    };

    const ensureCurrentTempleOption = useCallback(
        async (id?: string) => {
            if (!id) return;
            try {
                const res = await networkInstance.clientWithHeaders.shrineApi.getTemple(id);
                const t = res?.data?.data;
                if (!t) return;
                setTemples(prev => {
                    if (prev.some(p => String(p.id) === String(id))) return prev;
                    return [{ id: String(t.id), temple_name: t.temple_name }, ...prev];
                });
            } catch { }
        },
        [networkInstance.clientWithHeaders.shrineApi]
    );

    const loadAllTemples = useCallback(async () => {
        if (!shouldShowTempleDropdown) return;
        setLoadingTemples(true);
        try {
            const limit = 200;
            let offset = 0;
            let total = Infinity;
            const acc: TTemple[] = [];
            let safety = 0;

            while (offset < total && safety < 50) {
                const res = await networkInstance.clientWithHeaders.shrineApi.getTemples(
                    { limit, offset },
                    undefined,
                    undefined,
                    countryState?.countryCode,
                    languageState?.monetisationLanguage
                );
                const { items, count } = parseTempleList(res);
                total = count || 0;
                if (!items.length) break;
                items.forEach(t => acc.push({ id: String(t.id), temple_name: t.temple_name }));
                offset += limit;
                safety += 1;
            }

            const dedup = Array.from(new Map(acc.map(t => [t.id, t])).values());
            setTemples(dedup);

            const selectedId = normalizeId(formData?.mandir_id || form.getFieldValue("mandir_id"));
            if (selectedId && !dedup.some(t => t.id === selectedId)) {
                await ensureCurrentTempleOption(selectedId);
            }

            if (selectedId) form.setFieldsValue({ mandir_id: selectedId });
        } finally {
            setLoadingTemples(false);
        }
    }, [
        shouldShowTempleDropdown,
        networkInstance.clientWithHeaders.shrineApi,
        countryState?.countryCode,
        languageState?.monetisationLanguage,
        form,
        formData,
        ensureCurrentTempleOption,
    ]);

    useEffect(() => {
        if (!shouldShowTempleDropdown) return;
        const initial = normalizeId(formData?.mandir_id || form.getFieldValue("mandir_id"));
        if (!initial) return;
        let cancelled = false;
        (async () => {
            await ensureCurrentTempleOption(initial);
            if (!cancelled) form.setFieldsValue({ mandir_id: initial });
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shouldShowTempleDropdown, formData?.mandir_id, ensureCurrentTempleOption]);

    useEffect(() => {
        if (shouldShowTempleDropdown) loadAllTemples();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shouldShowTempleDropdown]);

    useEffect(() => {
        if (!shouldShowTempleDropdown) return;
        const current = normalizeId(form.getFieldValue("mandir_id"));
        if (current) form.setFieldsValue({ mandir_id: current });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [temples]);

    /** ========= Prune helpers ========= */
    const isPlainObject = (x: any) => Object.prototype.toString.call(x) === "[object Object]";
    const pruneEmptyDeep = (obj: any): any => {
        if (Array.isArray(obj)) {
            const arr = obj.map(pruneEmptyDeep).filter(v =>
                !(v === undefined || v === null || v === "" || (isPlainObject(v) && Object.keys(v).length === 0) || (Array.isArray(v) && v.length === 0))
            );
            return arr;
        } else if (isPlainObject(obj)) {
            const out: any = {};
            Object.keys(obj).forEach(k => {
                const v = pruneEmptyDeep(obj[k]);
                const drop =
                    v === undefined || v === null || v === "" ||
                    (isPlainObject(v) && Object.keys(v).length === 0) ||
                    (Array.isArray(v) && v.length === 0);
                if (!drop) out[k] = v;
            });
            return out;
        }
        return obj;
    };

    /** ========= Ensure New variant is removed from form store if untouched ========= */
    const handleSubmitPrep = useCallback(() => {
        form.submit();
    }, [form]);

    /** ---------- NEW VARIANT grouped into grey cards (conditionally required) ---------- */

    const NewBlock = useMemo(
        () => (
            <>
                <FieldCard>
                    <CardTitle>Festival / Tithi Tag</CardTitle>
                    <FieldRow>
                        <Form.Item
                            name={["promotional_avatar_details", "content_exp", "default", "festival"]}
                            label="Hindi"
                            rules={REQUIRED_RULE}
                            dependencies={[["promotional_avatar_details", "content_exp"], ["promotional_avatar_details", "cta_exp"]]}
                        >
                            <Input />
                        </Form.Item>
                        <Form.Item
                            name={["promotional_avatar_details", "content_exp", "english", "festival"]}
                            label="English"
                            rules={REQUIRED_RULE}
                            dependencies={[["promotional_avatar_details", "content_exp"], ["promotional_avatar_details", "cta_exp"]]}
                        >
                            <Input />
                        </Form.Item>
                    </FieldRow>
                </FieldCard>

                <FieldCard>
                    <CardTitle>Header (Title)</CardTitle>
                    <FieldRow>
                        <Form.Item
                            name={["promotional_avatar_details", "content_exp", "default", "title"]}
                            label="Hindi"
                            rules={REQUIRED_RULE}
                            dependencies={[["promotional_avatar_details", "content_exp"], ["promotional_avatar_details", "cta_exp"]]}
                        >
                            <Input />
                        </Form.Item>
                        <Form.Item
                            name={["promotional_avatar_details", "content_exp", "english", "title"]}
                            label="English"
                            rules={REQUIRED_RULE}
                            dependencies={[["promotional_avatar_details", "content_exp"], ["promotional_avatar_details", "cta_exp"]]}
                        >
                            <Input />
                        </Form.Item>
                    </FieldRow>
                </FieldCard>

                <FieldCard>
                    <CardTitle>Subtitle</CardTitle>
                    <FieldRow>
                        <Form.Item
                            name={["promotional_avatar_details", "content_exp", "default", "subtitle"]}
                            label="Hindi"
                            rules={REQUIRED_RULE}
                            dependencies={[["promotional_avatar_details", "content_exp"], ["promotional_avatar_details", "cta_exp"]]}
                        >
                            <Input />
                        </Form.Item>
                        <Form.Item
                            name={["promotional_avatar_details", "content_exp", "english", "subtitle"]}
                            label="English"
                            rules={REQUIRED_RULE}
                            dependencies={[["promotional_avatar_details", "content_exp"], ["promotional_avatar_details", "cta_exp"]]}
                        >
                            <Input />
                        </Form.Item>
                    </FieldRow>
                </FieldCard>

                <FieldCard>
                    <CardTitle>Description</CardTitle>
                    <FieldRow>
                        <Form.Item
                            name={["promotional_avatar_details", "content_exp", "default", "description"]}
                            label="Hindi"
                            rules={REQUIRED_RULE}
                            dependencies={[["promotional_avatar_details", "content_exp"], ["promotional_avatar_details", "cta_exp"]]}
                        >
                            <TextArea />
                        </Form.Item>
                        <Form.Item
                            name={["promotional_avatar_details", "content_exp", "english", "description"]}
                            label="English"
                            rules={REQUIRED_RULE}
                            dependencies={[["promotional_avatar_details", "content_exp"], ["promotional_avatar_details", "cta_exp"]]}
                        >
                            <TextArea />
                        </Form.Item>
                    </FieldRow>
                </FieldCard>

                <FieldCard>
                    <CardTitle>CTA</CardTitle>
                    <FieldRow>
                        <Form.Item
                            name={["promotional_avatar_details", "cta_exp", "default_text"]}
                            label="CTA Text (Hindi)"
                            rules={REQUIRED_RULE}
                            dependencies={[["promotional_avatar_details", "content_exp"], ["promotional_avatar_details", "cta_exp"]]}
                        >
                            <Input />
                        </Form.Item>
                        <Form.Item
                            name={["promotional_avatar_details", "cta_exp", "english_text"]}
                            label="CTA Text (English)"
                            rules={REQUIRED_RULE}
                            dependencies={[["promotional_avatar_details", "content_exp"], ["promotional_avatar_details", "cta_exp"]]}
                        >
                            <Input />
                        </Form.Item>
                    </FieldRow>
                    <Form.Item
                        name={["promotional_avatar_details", "cta_exp", "deeplink"]}
                        label="Deeplink"
                        rules={REQUIRED_RULE}
                        dependencies={[["promotional_avatar_details", "content_exp"], ["promotional_avatar_details", "cta_exp"]]}
                        style={{ marginTop: 8 }}
                    >
                        <Input />
                    </Form.Item>
                </FieldCard>
            </>
        ),
        [REQUIRED_RULE]
    );


    return (
        <>
            <DrawerBodyFix />
            <div style={{ position: "relative", paddingBottom: 56 }}>
                {/* Basics */}
                <Form.Item name={["code"]} label="Code" rules={REQUIRED_RULE}>
                    <Input disabled={!!formData} />
                </Form.Item>

                {shouldShowTempleDropdown && (
                    <Form.Item name={"mandir_id"} label={"Temple"} extra="Select the temple to link this avatar (posts mandir_id).">
                        <Select
                            showSearch
                            allowClear
                            placeholder="Search & select temple"
                            loading={loadingTemples}
                            filterOption={(input, option) =>
                                String((option?.props as any)?.children || "").toLowerCase().includes(input.toLowerCase())
                            }
                            onDropdownVisibleChange={(open) => {
                                if (open && !temples.length) loadAllTemples();
                            }}
                        >
                            {temples.map((t) => (
                                <Select.Option key={t.id} value={t.id}>
                                    {t.temple_name}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                )}

                <Form.Item name={["type"]} label="Type" rules={REQUIRED_RULE} initialValue={"default"}>
                    <Radio.Group
                        onChange={(e) => {
                            if (e.target.value === "default") {
                                form.setFieldValue(["access_type"], "free");
                            }
                        }}
                        disabled={type === "temple" || !!formData}
                    >
                        <Radio value="default">Default</Radio>
                        <Radio value="layered">Layered</Radio>
                        <Radio disabled value="temple">Temple</Radio>
                        <Radio value="promotional">Promotional</Radio>
                    </Radio.Group>
                </Form.Item>

                <Form.Item name={["position"]} label="Position" rules={REQUIRED_RULE}>
                    <InputNumber min={0} />
                </Form.Item>

                <Form.Item name={"access_type"} label="Access type" rules={REQUIRED_RULE} initialValue={"free"}>
                    <Select disabled={(type !== 'layered' && type !== 'promotional') || isLockedCore}>
                        <Select.Option disabled value={"paid"}>{'Paid'}</Select.Option>
                        <Select.Option value={"free"}>{'Free'}</Select.Option>
                    </Select>
                </Form.Item>

                <SimpleImageUplaod
                    disabled={type === "temple"}
                    allowUploadUsingUrl
                    getS3PathApi={s3UplaodApi}
                    name={["attachment"]}
                    form={form}
                    rules={REQUIRED_RULE}
                />

                <Form.Item name={["names", "default", 0]} label="Name (Hindi)" rules={REQUIRED_RULE}>
                    <Input disabled={type === "temple"} />
                </Form.Item>
                <Form.Item name={["names", "english", 0]} label="Name (English)" rules={REQUIRED_RULE}>
                    <Input disabled={type === "temple"} />
                </Form.Item>

                <Form.Item
                    name={["available_for_purchase"]}
                    label="Available for purchase"
                    rules={REQUIRED_RULE}
                    initialValue={false}
                    valuePropName="checked"
                >
                    <Checkbox disabled={access_type !== "paid"} />
                </Form.Item>

                {access_type === "paid" && (
                    <>
                        <SimpleImageUplaod allowUploadUsingUrl getS3PathApi={s3UplaodApi} name={["paid_avatar_details", "default_attachment"]} form={form} />
                        <Form.Item name={["paid_avatar_details", "pricing", "INR"]} label="Pricing INR" rules={REQUIRED_RULE}>
                            <InputNumber />
                        </Form.Item>
                        <Form.Item name={["paid_avatar_details", "pricing", "USD"]} label="Pricing USD" rules={REQUIRED_RULE}>
                            <InputNumber />
                        </Form.Item>
                        <SimpleImageUplaod allowUploadUsingUrl defaultUploadType={UPLOAD_TYPES.LINK} rules={REQUIRED_RULE} accept="audio/*" getS3PathApi={s3UplaodApi} name={["paid_avatar_details", "playback_audio"]} form={form} />
                        <SimpleImageUplaod allowUploadUsingUrl getS3PathApi={s3UplaodApi} name={["paid_avatar_details", "playback_audio_image"]} form={form} rules={REQUIRED_RULE} />
                        <Form.Item name={["paid_avatar_details", "content", "default", "title"]} label="Title default" rules={REQUIRED_RULE}>
                            <Input />
                        </Form.Item>
                        <Form.Item name={["paid_avatar_details", "content", "default", "subtitle"]} label="Subtitle default" rules={REQUIRED_RULE}>
                            <Input />
                        </Form.Item>
                        <Form.Item name={["paid_avatar_details", "content", "default", "description"]} label="Description default" rules={REQUIRED_RULE}>
                            <TextArea />
                        </Form.Item>
                        <Form.Item name={["paid_avatar_details", "content", "english", "title"]} label="Title english" rules={REQUIRED_RULE}>
                            <Input />
                        </Form.Item>
                        <Form.Item name={["paid_avatar_details", "content", "english", "subtitle"]} label="Subtitle english" rules={REQUIRED_RULE}>
                            <Input />
                        </Form.Item>
                        <Form.Item name={["paid_avatar_details", "content", "english", "description"]} label="Description english" rules={REQUIRED_RULE}>
                            <TextArea />
                        </Form.Item>
                        <SimpleImageUplaod allowUploadUsingUrl getS3PathApi={s3UplaodApi} name={["paid_avatar_details", "lock_icon"]} form={form} />
                        <SimpleImageUplaod allowUploadUsingUrl getS3PathApi={s3UplaodApi} name={["paid_avatar_details", "curtain_unlocked"]} form={form} />
                        <SimpleImageUplaod allowUploadUsingUrl getS3PathApi={s3UplaodApi} name={["paid_avatar_details", "curtain_locked"]} form={form} />
                    </>
                )}

                {/* Promotional sections */}
                {type === "promotional" && (
                    <>
                        <SimpleImageUplaod
                            accept="audio/*"
                            allowUploadUsingUrl
                            getS3PathApi={s3UplaodApi}
                            name={["promotional_avatar_details", "audio_attachment"]}
                            form={form}
                        />

                        <VariantPanel>
                            <div style={{ display: "block" }}>{NewBlock}</div>
                        </VariantPanel>

                        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8 }}>
                            <div style={{ fontWeight: 600 }}>Target config</div>
                            <a
                                onClick={() => {
                                    const val = form.getFieldValue(["promotional_avatar_details", "target_config"]);
                                    form.setFieldsValue({
                                        promotional_avatar_details: {
                                            ...(form.getFieldValue(["promotional_avatar_details"]) || {}),
                                            target_config: formatJson(val),
                                        },
                                    });
                                }}
                            >
                                Pretty
                            </a>
                        </div>
                        <Form.Item
                            name={["promotional_avatar_details", "target_config"]}
                            rules={[{ required: true, message: "Target config is required" }]}
                        >
                            <TextArea rows={4} placeholder='{"min_age":1,"max_age":10,"min_cohort":1,"max_cohort":100}' />
                        </Form.Item>
                    </>
                )}

                <Form.Item name={["active"]} label="Active" rules={REQUIRED_RULE} initialValue={false} valuePropName="checked">
                    <Checkbox />
                </Form.Item>

                {/* ---------- Share Media Previews (from metadata.share_media) ---------- */}
                {/* We do NOT bind these to the form's name to avoid sending metadata back.
          They are purely previews, resolving from live form value (if any) or formData fallback. */}
                <Row gutter={16}>
                    <Col xs={24} md={12}>
                        <Form.Item label="Shareable Media (English)">
                            {renderImage(enPreviewUrl)}
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item label="Shareable Media (Hindi)">
                            {renderImage(hiPreviewUrl)}
                        </Form.Item>
                    </Col>
                </Row>

                {showSubmitBtn && (
                    <SubmitButton
                        type="primary"
                        htmlType="button"
                        onClick={handleSubmitPrep}
                    >
                        {submitLabel}
                    </SubmitButton>
                )}
            </div>
        </>
    );
};

export default CreateAvatarForm;
